import { Component, OnInit } from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, FormBuilder, FormGroup, Validators, AbstractControl} from '@angular/forms';
import {Esito, GenericErrorResponse, GenericResponse} from 'src/app/api-interfaces';
import { AuthService } from 'src/app/auth/auth.service';
import { ADDRESS_TYPE_CHOICES } from 'src/app/shared/address-type';
import { NotificationService, NotificationType } from 'src/app/shared/notification.service';
import { IdentityService } from '../identity.service';
import { RequestService } from '../requests/request.service';
import { differenceInCalendarDays } from 'date-fns';
import { ValidatorService } from 'src/app/shared/validator.service';
import { ErrorService } from 'src/app/shared/errors.service';
import {finalize, map, startWith, tap} from 'rxjs/operators';
import { Request } from '../requests/request.model';
import {EditIdentityService} from '../edit-identity/edit-identity.service';
import {combineLatest, Observable} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-compile-draft',
  templateUrl: './compile-draft.component.html',
  styleUrls: ['./compile-draft.component.sass'],
  providers: [EditIdentityService]
})
export class CompileDraftComponent implements OnInit {
  readonly issuers$ = this.editIdentityService.issuers$;
  readonly areLoadingIssuers$ = this.editIdentityService.areLoadingIssuers$;
  readonly canLoadMoreIssuer$ = this.editIdentityService.canLoadMoreIssuer$;
  readonly showIssuerSpinner$ = combineLatest([this.areLoadingIssuers$, this.canLoadMoreIssuer$]).pipe(map(([areLoadingIssuers, canLoadMoreIssuer]) => areLoadingIssuers || canLoadMoreIssuer));

  readonly birthCountries$ = this.editIdentityService.birthCountries$;
  readonly areLoadingBirthCountries$ = this.editIdentityService.areLoadingBirthCountries$;
  readonly canLoadMoreBirthCountry$ = this.editIdentityService.canLoadMoreBirthCountry$;
  readonly showBirthCountrySpinner$ = combineLatest([this.areLoadingBirthCountries$, this.canLoadMoreBirthCountry$]).pipe(map(([areLoadingBirthCountries, canLoadMoreCountry]) => areLoadingBirthCountries || canLoadMoreCountry));
  readonly birthProvincies$ = this.editIdentityService.birthProvincies$;
  readonly areLoadingBirthProvincies$ = this.editIdentityService.areLoadingBirthProvincies$;
  readonly canLoadMoreBirthProvince$ = this.editIdentityService.canLoadMoreBirthProvince$;
  readonly showBirthProvinceSpinner$ = combineLatest([this.areLoadingBirthProvincies$, this.canLoadMoreBirthProvince$]).pipe(map(([areLoadingBirthProvincies, canLoadMoreBirthProvince]) => areLoadingBirthProvincies || canLoadMoreBirthProvince));
  readonly birthCities$ = this.editIdentityService.birthCities$;
  readonly areLoadingBirthCities$ = this.editIdentityService.areLoadingBirthCities$;
  readonly canLoadMoreBirthCity$ = this.editIdentityService.canLoadMoreBirthCity$;
  readonly showBirthCitySpinner$ = combineLatest([this.areLoadingBirthCities$, this.canLoadMoreBirthCity$]).pipe(map(([areLoadingBirthCities, canLoadMoreBirthCity]) => areLoadingBirthCities || canLoadMoreBirthCity));

  readonly countries$ = this.editIdentityService.countries$;
  readonly areLoadingCountries$ = this.editIdentityService.areLoadingCountries$;
  readonly canLoadMoreCountry$ = this.editIdentityService.canLoadMoreCountry$;
  readonly showCountrySpinner$ = combineLatest([this.areLoadingCountries$, this.canLoadMoreCountry$]).pipe(map(([areLoadingCountries, canLoadMoreCountry]) => areLoadingCountries || canLoadMoreCountry));
  readonly provincies$ = this.editIdentityService.provincies$;
  readonly areLoadingProvincies$ = this.editIdentityService.areLoadingProvincies$;
  readonly canLoadMoreProvince$ = this.editIdentityService.canLoadMoreProvince$;
  readonly showProvinceSpinner$ = combineLatest([this.areLoadingProvincies$, this.canLoadMoreProvince$]).pipe(map(([areLoadingProvincies, canLoadMoreProvince]) => areLoadingProvincies || canLoadMoreProvince));
  readonly cities$ = this.editIdentityService.cities$;
  readonly areLoadingCities$ = this.editIdentityService.areLoadingCities$;
  readonly canLoadMoreCity$ = this.editIdentityService.canLoadMoreCity$;
  readonly showCitySpinner$ = combineLatest([this.areLoadingCities$, this.canLoadMoreCity$]).pipe(map(([areLoadingCities, canLoadMoreCity]) => areLoadingCities || canLoadMoreCity));

  draftToCompile: Request;
  validateForm: UntypedFormGroup;
  isSpinning = false;
  addressTypes: { key: string, label: string }[];
  objTelChangeInput: any;
  objTelConfirmInput: any;
  public phone = '';
  showFiscalCodeError = false;
  fiscalCodeErrorMsg = 'Qualcosa è andato storto nella verifica del codice fiscale';


  constructor(
    private fb: UntypedFormBuilder,
    public requestService: RequestService,
    private identityService: IdentityService,
    public notificationService: NotificationService,
    public authService: AuthService,
    private validatorService: ValidatorService,
    private errorService: ErrorService,
    private editIdentityService: EditIdentityService
    )
  {
    this.validateForm = this.fb.group({
      personalData: this.fb.group({
        firstName: [ null, Validators.required],
        lastName: [ null, Validators.required],
        sex: [ null, Validators.required],
        birthDate: [ null, Validators.required],
        birthCountry: [ null, Validators.required],
        birthProvince: [ null, Validators.required],
        birthCity: [ null, Validators.required],
        fiscalCode: [ null, Validators.required],
        fiscalCodeCardId: [ null, [Validators.required, Validators.minLength(20), Validators.maxLength(20)]],
        fiscalCodeCardExpirationDate: [ null, Validators.required],
      }),
      residence: this.fb.group({
        country: [ null, Validators.required],
        province: [ null, Validators.required],
        city: [ null, Validators.required],
        addressType: [ null, Validators.required],
        addressName: [ null, Validators.required],
        addressNumber: [ null, Validators.required],
        zipCode: [ null, [Validators.required, Validators.maxLength(5), Validators.minLength(5)]],
      }),
      identityDocument: this.fb.group ({
        idDocumentType: [ null, Validators.required],
        idDocumentIssuerName: [ null, Validators.required],
        idDocumentExpirationDate: [ null, Validators.required],
        idDocumentIssueDate: [ null, Validators.required],
        idDocumentIssuerType: [ null, Validators.required],
        idDocumentNumber: [ null, Validators.required],
      }),
      contact: this.fb.group({
        phone: [null, Validators.required],
        email: [null, Validators.required],
        pec: [null],
        webhook: [null],
      })
    });
    this.addressTypes = ADDRESS_TYPE_CHOICES;
  }

  ngOnInit(): void {
    this.requestService.isSpinning.next(true);

    this.requestService.draftToCompile.subscribe(value => {
      this.draftToCompile = value;
      this.validateForm.controls.personalData.patchValue({
        fiscalCode: this.draftToCompile.fiscalCode,
      });
      this.validateForm.controls.contact.patchValue({
        email: this.draftToCompile.email,
      });
    });

    this.onIssuerValueChange$().subscribe();
    this.registerOnIssuerTypeChangeStatus$().subscribe();
    this.registerOnIssuerTypeChangeValue$().subscribe();

    this.onBirthCountryValueChange$().subscribe();
    this.registerOnBirthCountryStatusChange$().subscribe();
    this.registerOnBirthCountryValueChange$().subscribe();

    this.onBirthProvinceValueChange$().subscribe();
    this.registerOnBirthProvinceStatusChange$().subscribe();
    this.registerOnBirthProvinceValueChange$().subscribe();

    this.onBirthCityValueChange$().subscribe();

    this.onCountryValueChange$().subscribe();
    this.registerOnCountryStatusChange$().subscribe();
    this.registerOnCountryValueChange$().subscribe();

    this.onProvinceValueChange$().subscribe();
    this.registerOnProvinceStatusChange$().subscribe();
    this.registerOnProvinceValueChange$().subscribe();

    this.onCityValueChange$().subscribe();
  }

  getFormGroup(name: string = 'personalData'): FormGroup {
    return this.validateForm.get(name) as FormGroup;
  }

  convertDate(date: string): string {
    return new Date(date).toLocaleDateString('fr-ca', { timeZone: 'Europe/Rome' });
  }

  compileDraft(): void {
    this.isSpinning = true;

    const payload = {
      requestId: this.draftToCompile.id,

      phoneNumber: this.validateForm.get('contact').get('phone').value,
      firstName: this.validateForm.get('personalData').get('firstName').value,
      lastName: this.validateForm.get('personalData').get('lastName').value,
      sex: this.validateForm.get('personalData').get('sex').value,
      birthDate: this.convertDate(this.validateForm.get('personalData').get('birthDate').value),
      birthCountry: this.validateForm.get('personalData').get('birthCountry').value,
      birthProvince: this.validateForm.get('personalData').get('birthProvince').value,
      birthCity: this.validateForm.get('personalData').get('birthCity').value,
      fiscalCodeCardId: this.validateForm.get('personalData').get('fiscalCodeCardId').value,
      fiscalCodeCardExpirationDate: this.convertDate(this.validateForm.get('personalData').get('fiscalCodeCardExpirationDate').value),

      country: this.validateForm.get('residence').get('country').value,
      province: this.validateForm.get('residence').get('province').value,
      city: this.validateForm.get('residence').get('city').value,
      postalCode: this.validateForm.get('residence').get('zipCode').value,
      addressType: this.validateForm.get('residence').get('addressType').value,
      addressName: this.validateForm.get('residence').get('addressName').value,
      addressNumber: this.validateForm.get('residence').get('addressNumber').value,

      idDocumentType: this.validateForm.get('identityDocument').get('idDocumentType').value,
      idDocumentExpirationDate: this.convertDate(this.validateForm.get('identityDocument').get('idDocumentExpirationDate').value),
      idDocumentIssueDate: this.convertDate(this.validateForm.get('identityDocument').get('idDocumentIssueDate').value),
      idDocumentIssuerName: this.validateForm.get('identityDocument').get('idDocumentIssuerName').value,
      idDocumentIssuerType: this.validateForm.get('identityDocument').get('idDocumentIssuerType').value,
      idDocumentNumber: this.validateForm.get('identityDocument').get('idDocumentNumber').value,

      pec: this.validateForm.get('contact').get('pec').value,
      webhook: this.validateForm.get('contact').get('webhook').value,
    };

    this.showFiscalCodeError = false;

    this.identityService.compileDraft(payload).pipe(
      finalize(() => this.isSpinning = false)
    ).subscribe(
      (response: GenericResponse) => {
        if (response.esito === Esito.Ok) {
          this.notificationService.createNotification(
            NotificationType.SUCCESS,
            'Richiesta creata con successo.',
            ''
          );
          this.identityService.currentViewChanged.next('requests');
        }
      },
      (err: any) => {
        const error: GenericErrorResponse = err.error;
        const serializationErrors: any = error.result.serialization_errors;
        if (serializationErrors) {
          Object.keys(serializationErrors).forEach( (key: string) => {
            if (key === 'fiscalCode') {
              this.showFiscalCodeError = true;
              this.fiscalCodeErrorMsg = serializationErrors.fiscalCode[0].message;
              return;
            }
            try {
              const errorDescription = serializationErrors[key][0].message;
              const fieldControl = this.validatorService.mappingSerializationFieldCompileDraft.get(key);
              this.validateForm.controls[fieldControl].get(key).setErrors({
                error: errorDescription
              });
            } catch (error){
              this.isSpinning = false;
            }
          });
        }
        this.notificationService.createNotification(
          NotificationType.ERROR,
          'Errore',
          this.errorService.getErrorBycode(error.result.error_code).message
        );
      }
    );
  }

  lookUpCountries(query: string, isBirthField = false): void {
    isBirthField ? this.editIdentityService.onBirthCountrySearch(query) : this.editIdentityService.onCountrySearch(query);
  }

  lookUpProvinces(query: string, isBirthField = false): void {
    isBirthField ? this.editIdentityService.onBirthProvinceSearch(query) : this.editIdentityService.onProvinceSearch(query);
  }

  lookUpCities(query: string, isBirthField = false): void {
    isBirthField ? this.editIdentityService.onBirthCitySearch(query) : this.editIdentityService.onCitySearch(query);
  }

  disabledPastDate = (current: Date): boolean => differenceInCalendarDays(current, new Date()) <= 0;
  disabledFutureDate = (current: Date): boolean => differenceInCalendarDays(current, new Date()) > 0;

  onCountryChange(isMobileChange: boolean = true, data: any): void {
    this.onValidatePhone(isMobileChange);
    this.validateForm.controls.contact.patchValue({
      phone: this.phone,
    });
  }

  onValidatePhone(isMobileChange: boolean = true): void{
    if (isMobileChange) {
      this.phone = this.objTelChangeInput.a.value;
    }
  }

  telInputObject(isMobileChange: boolean = true, obj: any): void {
    if (isMobileChange){
      this.objTelChangeInput = obj;
    } else {
      this.objTelConfirmInput = obj;
    }
  }

  getErrorFromSerializationField(fieldControlName: string, fieldName: string, defaultMessage: string = 'Qualcosa è andato storto con la validazione'): string {
    const field = this.validateForm.controls[fieldControlName].get(fieldName);
    return this.validatorService.getErrorFromSerializationField(field, defaultMessage);
  }

  private onCountryValueChange$(): Observable<any> {
    return this.editIdentityService.onCountryValueChange$().pipe(untilDestroyed(this));
  }

  get countryCtrl(): AbstractControl {
    return this.validateForm.get(['residence', 'country']);
  }

  get countryValue(): string {
    return this.countryCtrl.value;
  }

  get provinceCtrl(): AbstractControl {
    return this.validateForm.get(['residence', 'province']);
  }

  get provinceValue(): string {
    return this.provinceCtrl.value;
  }

  get cityCtrl(): AbstractControl {
    return this.validateForm.get(['residence', 'city']);
  }

  get cityValue(): string {
    return this.cityCtrl.value;
  }

  private registerOnCountryStatusChange$(): Observable<any> {
    return this.countryCtrl.statusChanges.pipe(
      startWith(this.countryCtrl.status),
      tap(status => {
        if (status === 'INVALID') {
          this.provinceCtrl.disable();
          this.cityCtrl.disable();
        } else if (status === 'VALID') {
          this.provinceCtrl.enable();
        }
      })
    );
  }

  private registerOnCountryValueChange$(): Observable<any> {
    return this.countryCtrl.valueChanges.pipe(
      tap(() => {
        this.editIdentityService.provincies = [];
        this.editIdentityService.onProvinceSearch('');
        this.editIdentityService.cities = [];
        this.editIdentityService.onCitySearch('');
        this.provinceCtrl.patchValue(null, {emitEvent: false});
        this.cityCtrl.patchValue(null, {emitEvent: false});
      })
    );
  }

  private registerOnProvinceStatusChange$(): Observable<any> {
    return this.provinceCtrl.statusChanges.pipe(
      startWith(this.provinceCtrl.status),
      tap(status => {
        if (status === 'INVALID') {
          this.cityCtrl.disable();
        } else if (status === 'VALID') {
          this.cityCtrl.enable();
        }
      })
    );
  }

  private registerOnProvinceValueChange$(): Observable<any> {
    return this.provinceCtrl.valueChanges.pipe(
      tap(() => {
        this.editIdentityService.cities = [];
        this.editIdentityService.onCitySearch('');
        this.cityCtrl.patchValue(null, {emitEvent: false});
      })
    );
  }

  private onProvinceValueChange$(): Observable<any> {
    return this.editIdentityService.onProvinceValueChange$(this.countryCtrl).pipe(untilDestroyed(this));
  }

  private onCityValueChange$(): Observable<any> {
    return this.editIdentityService.onCityValueChange$(this.countryCtrl, this.provinceCtrl).pipe(untilDestroyed(this));
  }

  loadMoreCountries(isBirthField = false): void {
    isBirthField ? this.editIdentityService.loadMoreBirthCountries() : this.editIdentityService.loadMoreCountries();
  }

  loadMoreProvincies(isBirthField = false): void {
    isBirthField ? this.editIdentityService.loadMoreBirthProvincies(this.countryValue) : this.editIdentityService.loadMoreProvincies(this.countryValue);
  }

  loadMoreCities(isBirthField = false): void {
    isBirthField ? this.editIdentityService.loadMoreBirthCities(this.countryValue, this.provinceValue) : this.editIdentityService.loadMoreCities(this.countryValue, this.provinceValue);
  }

  private onIssuerValueChange$(): Observable<any> {
    return this.editIdentityService.onIssuerValueChange$(this.issuerTypeCtrl).pipe(untilDestroyed(this));
  }

  get issuerTypeCtrl(): AbstractControl {
    return this.validateForm.get(['identityDocument', 'idDocumentIssuerType']);
  }

  get issuerType(): string {
    return this.issuerTypeCtrl.value;
  }

  get issuerNameCtrl(): AbstractControl {
    return this.validateForm.get(['identityDocument', 'idDocumentIssuerName']);
  }

  get issuerName(): string {
    return this.issuerNameCtrl.value;
  }

  loadMoreIssuer(): void {
    this.editIdentityService.loadMoreIssuer(this.issuerType);
  }

  private registerOnIssuerTypeChangeStatus$(): Observable<any> {
    return this.issuerTypeCtrl.statusChanges.pipe(
      startWith(this.issuerTypeCtrl.status),
      tap(status => {
        if (status === 'INVALID') {
          this.issuerNameCtrl.disable();
        } else if (status === 'VALID') {
          this.issuerNameCtrl.enable();
        }
      })
    );
  }

  private registerOnIssuerTypeChangeValue$(): Observable<any> {
    return this.issuerTypeCtrl.valueChanges.pipe(
      tap(() => this.issuerNameCtrl.patchValue(null))
    );
  }










  private onBirthCountryValueChange$(): Observable<any> {
    return this.editIdentityService.onBirthCountryValueChange$().pipe(untilDestroyed(this));
  }

  get birthCountryCtrl(): AbstractControl {
    return this.validateForm.get(['personalData', 'birthCountry']);
  }

  get birthCountryValue(): string {
    return this.birthCountryCtrl.value;
  }

  get birthProvinceCtrl(): AbstractControl {
    return this.validateForm.get(['personalData', 'birthProvince']);
  }

  get birthProvinceValue(): string {
    return this.birthProvinceCtrl.value;
  }

  get birthCityCtrl(): AbstractControl {
    return this.validateForm.get(['personalData', 'birthCity']);
  }

  get birthCityValue(): string {
    return this.birthCityCtrl.value;
  }

  private registerOnBirthCountryStatusChange$(): Observable<any> {
    return this.birthCountryCtrl.statusChanges.pipe(
      startWith(this.birthCountryCtrl.status),
      tap(status => {
        if (status === 'INVALID') {
          this.birthProvinceCtrl.disable();
          this.birthCityCtrl.disable();
        } else if (status === 'VALID') {
          this.birthProvinceCtrl.enable();
        }
      })
    );
  }

  private registerOnBirthCountryValueChange$(): Observable<any> {
    return this.birthCountryCtrl.valueChanges.pipe(
      tap(() => {
        this.editIdentityService.birthProvincies = [];
        this.editIdentityService.onBirthProvinceSearch('');
        this.editIdentityService.birthCities = [];
        this.editIdentityService.onBirthCitySearch('');
        this.birthProvinceCtrl.patchValue(null, {emitEvent: false});
        this.birthCityCtrl.patchValue(null, {emitEvent: false});
      })
    );
  }

  private registerOnBirthProvinceStatusChange$(): Observable<any> {
    return this.birthProvinceCtrl.statusChanges.pipe(
      startWith(this.birthProvinceCtrl.status),
      tap(status => {
        if (status === 'INVALID') {
          this.birthCityCtrl.disable();
        } else if (status === 'VALID') {
          this.birthCityCtrl.enable();
        }
      })
    );
  }

  private registerOnBirthProvinceValueChange$(): Observable<any> {
    return this.birthProvinceCtrl.valueChanges.pipe(
      tap(() => {
        this.editIdentityService.birthCities = [];
        this.editIdentityService.onBirthCitySearch('');
        this.birthCityCtrl.patchValue(null, {emitEvent: false});
      })
    );
  }

  private onBirthProvinceValueChange$(): Observable<any> {
    return this.editIdentityService.onBirthProvinceValueChange$(this.birthCountryCtrl).pipe(untilDestroyed(this));
  }

  private onBirthCityValueChange$(): Observable<any> {
    return this.editIdentityService.onBirthCityValueChange$(this.birthCountryCtrl, this.birthProvinceCtrl).pipe(untilDestroyed(this));
  }
}
